import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { purchaseOrderService } from '../services/purchaseOrderService';
import { formatCurrency } from '../utils/currency';
import { raccourcisseurDeNumeros } from '../utils/numeroCommande';
import { useLanguage } from '../contexts/LanguageContext';
import { cle, useMessageTraduit } from '../utils/messageTraduit';
import EnteteEcran from '../components/commun/EnteteEcran';
import PiedTableau from '../components/commun/PiedTableau';
import { useFormatageDates } from '../utils/dates';
import { useStock } from '../contexts/StockContext';
import DialogueDetailCommande from '../components/commandes/DialogueDetailCommande';
import DialogueAjoutPaiement from '../components/commandes/DialogueAjoutPaiement';

const Accounting = ({ showSnackbar }) => {
  const { t } = useLanguage();
  const { formaterDateSaisie } = useFormatageDates();
  const { currentStock, triggerRefresh } = useStock();
  const [orders, setOrders] = useState([]);
  // ✅ PAGINATION SERVEUR, corrigée au lot RU4-d. Elle était LOCALE parce que la
  // route « ne paginait pas » — en réalité elle paginait, puis filtrait, puis
  // annonçait `total = len(orders)` et `pages = 1`. Le front repaginait donc un
  // sous-ensemble en le prenant pour le tout.
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [totaux, setTotaux] = useState([]);
  const [parPage, setParPage] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useMessageTraduit();
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  // 🔴 L'OUVERTURE VIENT DE L'URL, PAS D'UN `useState`. `viewMode` a disparu :
  // c'est lui qui rendait « précédent » inopérant — l'historique reculait et
  // l'écran ne bougeait pas, parce que l'état local survivait à la navigation.
  const navigate = useNavigate();
  const { id: commandeDetailleeId } = useParams();

  // ⚠️ RECHARGER QUAND LA PAGE CHANGE, sinon la pagination serveur ne pagine
  // rien : on demanderait toujours la page 1 en affichant un numéro de page
  // différent — un écran qui dit « page 3 » en montrant la première.
  useEffect(() => {
    loadAccountingOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, parPage]);

  // Recharger les données quand le stock change
  useEffect(() => {
    if (currentStock?.id) {
      loadAccountingOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStock?.id]);

  const loadAccountingOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await purchaseOrderService.getAccountingOrders({ page, per_page: parPage });
      setOrders(response.orders || []);
      setTotaux(response.totals || []);
      setTotal(response.total ?? 0);
      setPages(response.pages || 1);
    } catch (err) {
      // ⚠️ Repli mort retiré : `t()` rend toujours une chaîne truthy.
      setError(cle('accounting.loadError'));
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  // Le pré-remplissage du montant a quitté cet écran : il se DÉDUIT de la
  // commande, dans le dialogue partagé. C'était l'une des trois divergences
  // entre les deux copies — et celle qui manquait au chemin le plus naturel.
  const handleOpenPaymentDialog = (order) => {
    setSelectedOrder(order);
    setPaymentDialogOpen(true);
  };

  // On NAVIGUE : c'est l'URL qui ouvre le dialogue, et c'est elle qui le referme.
  const handleViewDetails = (order) => navigate(`/accounting/${order.id}`);

  const handleCloseDetail = () => navigate('/accounting');

  const handleClosePaymentDialog = () => {
    setPaymentDialogOpen(false);
    setSelectedOrder(null);
  };

  // Le dialogue construit la charge utile ; l'écran sait seulement OÙ l'envoyer
  // et quoi rafraîchir ensuite.
  const raccourcirNumero = raccourcisseurDeNumeros((orders || []).map((o) => o.order_number));

  const enregistrerPaiement = async (charge) => {
    await purchaseOrderService.createOrderPayment(selectedOrder.id, charge, true);
    await loadAccountingOrders();
    triggerRefresh();
  };




  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ ml: 0, mr: 0, pl: 0, pr: 0 }}>
      {/* L'icône collée au titre disparaît — cf. `OrderReception`. */}
      <EnteteEcran
        titre={t('accounting.title') || 'Comptabilité'}
        compteur={orders.length || undefined}
      />

      {/* ═══ LES TOTAUX, UN BANDEAU PAR DEVISE ══════════════════════════════
          🔴 ON NE SOMME PAS DEUX DEVISES. Un total qui mêle des shekels et des
          dollars est un nombre sans référent — et il a l'air d'un chiffre juste,
          ce qui est exactement le calcul refusé au tableau de bord.
          ⚠️ L'ÉTIQUETTE DE DEVISE N'APPARAÎT QUE S'IL Y EN A PLUS D'UNE : avec une
          seule, ce bandeau est identique à celui de la maquette, et la machinerie
          multi-devises reste invisible. Même doctrine que la TVA masquée et le
          bouton absent plutôt que grisé — *on ne montre pas la mécanique tant
          qu'elle ne sert à rien.*
          ⚠️ ET CES CHIFFRES VIENNENT DU SERVEUR, agrégés sur TOUTES les commandes
          non soldées. Calculés sur la liste chargée, ils auraient été justes sur
          leur sous-ensemble et faux comme totaux — le défaut le plus discret de
          ce lot, invisible tant que tout tient sur une page. */}
      {totaux.length > 0 && (
        <Box sx={{ mb: 3 }}>
          {totaux.map((bloc) => (
            <Box key={bloc.currency} sx={{ mb: 2 }}>
              {/* 🔴 L'ÉTIQUETTE DE DEVISE EST PORTÉE UNE FOIS PAR RANGÉE, pas par
                  case. Répétée sur les trois, c'est la MÊME information dite trois
                  fois sur une seule ligne — du bruit qui se lit comme une nuance.
                  ⚠️ Et elle n'apparaît que s'il y a plus d'une devise : avec une
                  seule, cette rangée est exactement la maquette. */}
              {totaux.length > 1 && (
                <Typography variant="overline" color="text.secondary"
                            sx={{ letterSpacing: 1, display: 'block', mb: 0.5 }}>
                  {bloc.currency}
                </Typography>
              )}
              <Grid container spacing={2}>
                {[
                  [t('accounting.totalInvoiced'), bloc.total_payable, null],
                  [t('accounting.totalSettled'), bloc.total_paid, 'success.main'],
                  [t('orders.remainingAmount'), bloc.remaining_amount, 'ambre'],
                ].map(([libelle, valeur, teinte]) => (
                  <Grid item xs={12} md={4} key={libelle}>
                    {/* 🔴 TROIS CARTES SÉPARÉES, POSÉES SUR LE FOND DE LA PAGE — et
                        la distinction avec le bandeau du DÉTAIL est réelle, pas
                        cosmétique. Là-bas, les cases se touchent parce qu'elles font
                        partie d'un panneau continu ; ici, le bandeau est posé
                        AU-DESSUS d'un tableau, sur la page. *Une surface dit à quoi
                        elle appartient : coller ces trois cartes les rattacherait au
                        tableau qu'elles surplombent.* Avec deux devises, la version
                        collée donnait six cases en grille — la lecture s'y perdait. */}
                    <Paper sx={{
                      p: 2.5, height: '100%',
                      ...(teinte === 'ambre' && {
                        bgcolor: 'warning.light',
                        color: (theme) => theme.palette.getContrastText(
                          theme.palette.warning.light),
                      }),
                    }}>
                      <Typography variant="body2"
                                  color={teinte === 'ambre' ? 'inherit' : 'text.secondary'}>
                        {libelle}
                      </Typography>
                      <Typography variant="h6" fontWeight="bold"
                                  color={teinte === 'ambre' ? 'inherit' : (teinte || 'text.primary')}>
                        {formatCurrency(valeur || 0, bloc.currency)}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </Box>
      )}

      {/* Le dialogue est monté avec `key={id}` : sans elle, passer d'une commande
          à l'autre sans refermer réutiliserait l'instance et garderait l'onglet
          et les données de la précédente. */}
      <DialogueDetailCommande
        key={commandeDetailleeId}
        id={commandeDetailleeId}
        ouvert={Boolean(commandeDetailleeId)}
        onFermer={handleCloseDetail}
        contexte={t('accounting.title')}
        showSnackbar={showSnackbar}
        onCommandeModifiee={loadAccountingOrders}
      />

      {orders.length === 0 ? (
        <Card>
          <CardContent>
            <Alert severity="info">
              {t('accounting.noPendingPayments') || 'Aucune commande en attente de paiement'}
            </Alert>
          </CardContent>
        </Card>
      ) : (
        <TableContainer 
          component={Paper} 
          sx={{ 
            ml: 0, 
            mr: 0,
            '& .MuiPaper-root': {
              padding: 0,
              margin: 0,
            }
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                {/* 🔴 MÊMES CONVENTIONS QUE LE TABLEAU DES COMMANDES : la date sous
                    le numéro, le numéro raccourci de ses segments communs. Cet écran
                    montre les MÊMES objets ; deux tableaux de commandes aux règles
                    différentes, dans le même logiciel, se lisent comme deux
                    logiciels. */}
                <TableCell>{t('orders.orderNumber')}</TableCell>
                <TableCell>{t('orders.supplier')}</TableCell>
                <TableCell align="right">{t('orders.finalTotal')}</TableCell>
                <TableCell align="right">{t('orders.totalPaid')}</TableCell>
                <TableCell align="right">{t('orders.remainingAmount')}</TableCell>
                <TableCell>{t('products.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.slice((page - 1) * parPage, page * parPage).map((order) => (
                <TableRow key={order.id} hover sx={{ cursor: 'pointer' }}
                          onClick={(e) => {
                            if (e.target.closest('button')) return;
                            handleViewDetails(order);
                          }}>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="bold"
                                title={order.order_number || ''}>
                      {raccourcirNumero(order.order_number)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {formaterDateSaisie(order.order_date)}
                    </Typography>
                  </TableCell>
                  <TableCell>{order.supplier_name}</TableCell>
                  <TableCell align="right">
                    <Typography variant="subtitle2" fontWeight="bold">
                      {formatCurrency(order.total_final || order.total_amount || 0, order.currency || 'EUR')}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="success.main">
                      {formatCurrency(order.total_paid || 0, order.currency || 'EUR')}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="subtitle2" fontWeight="bold" color="warning.main">
                      {formatCurrency(order.remaining_amount || 0, order.currency || 'EUR')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {/* 🔴 UN BOUTON TEXTE, PAS UNE ICÔNE. « + » ne dit pas ce qu'il
                        ajoute ; sur un écran comptable, le geste a un nom.
                        ⚠️ « ✓ Soldée » n'existe PAS ici, et c'est mesuré : la route
                        ne rend que les commandes dont il reste quelque chose à
                        payer — une commande réglée SORT de l'écran. Rendre cet état
                        demanderait d'élargir le périmètre de la Comptabilité, ce
                        qui est une décision de produit, pas de mise en forme. */}
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleOpenPaymentDialog(order)}
                    >
                      {t('accounting.collect')}
                    </Button>
                    <IconButton
                      color="info"
                      onClick={() => handleViewDetails(order)}
                      title={t('accounting.viewDetails') || 'Voir les détails'}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* 🔴 SECOND ÉCRAN SANS AUCUNE PAGINATION avant ce lot. */}
      {orders.length > 0 && (
        <PiedTableau
          total={total}
          page={page}
          pages={pages}
          parPage={parPage}
          onPage={setPage}
          onParPage={(n) => { setParPage(n); setPage(1); }}
          libelleTotal={`${total} ${(t('accounting.title') || 'Comptabilité').toLowerCase()}`}
          libelleParPage={t('common.perPage')}
        />
      )}

      {/* Dialog pour ajouter un paiement */}
      <DialogueAjoutPaiement
        commande={selectedOrder}
        ouvert={paymentDialogOpen}
        onFermer={handleClosePaymentDialog}
        onEnregistre={enregistrerPaiement}
        showSnackbar={showSnackbar}
      />
    </Box>
  );
};

export default Accounting;
